import React from 'react'
import styled from 'styled-components'
import { FormatMessageType, Options } from '../../../common/types'
import {
  Column,
  FormSubGroup,
  FormSubGroupHeader,
  FormSubGroupHeading,
  FormSubGroupWrapper,
  Row,
  Section,
  SectionColumn,
} from '../../../components/layout'
import {
  DateInput,
  RadioButtonGroup,
  SelectInput,
  TextAreaInput,
  TextInput,
  TimeInput,
} from '../../../components/layout/form/input'
import { translateInputOptions } from '../../../util/translateInputOptions'
import { serviceTypeOptions } from '../options'
import { SelectSectionColumn } from '../AddTowingOrder/TowingOrderDetailsSubFormContents'
import { useSettings } from '../../Parameters'

interface FormContentsProps {
  formatMessage: FormatMessageType
  showTowingDateSpan: boolean
}

interface DateSectionColumnProps {
  reverse?: boolean
}

interface DividerProps {
  alignSelfEnd?: boolean
}

export const DateSectionColumn = styled(SectionColumn as any)<DateSectionColumnProps>`
  display: flex;
  flex-direction: ${props => (props.reverse ? 'row-reverse' : 'row')};
  justify-content: space-between;
  align-items: flex-end;
`

export const HalfDateInput = styled(DateInput as any)`
  width: calc(50% - 1rem);
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    width: calc(50% - 0.125rem);
  }
`

export const HalfTimeInput = styled(TimeInput as any)`
  width: calc(50% - 1rem);
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    width: calc(50% - 0.125rem);
  }
`

const HalfTextInput = styled(TextInput as any)`
  width: calc(50% - 1rem);
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    width: calc(50% - 0.125rem);
  }
`

export const Divider = styled.div<DividerProps>`
  display: flex;
  align-items: center;
  align-self: ${props => props.alignSelfEnd ? 'flex-end' : 'auto'};
  height: ${props => props.theme.inputFieldHeight};
  padding-top: 3px;

  &::after {
    content: '—';
  }
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    display: none;
  }
  
`

export const orderScheduleOptions: Options = {
  options: {
    single: 'towingOrder.scheduling_fixed',
    span: 'towingOrder.scheduling_flexible',
  },
}

export const TowingOrderDetailsSubFormContents: React.FunctionComponent<FormContentsProps> = ({
  formatMessage,
  showTowingDateSpan,
}) => {
  const translatedOrderScheduleOptions = translateInputOptions(orderScheduleOptions, formatMessage)
  const translatedServiceTypeOptions = translateInputOptions(serviceTypeOptions, formatMessage)
  const settings = useSettings()

  if (!settings) {
    return null
  }

  return (
    <FormSubGroupWrapper>
      <FormSubGroupHeader>
        <Row>
          <Column>
            <FormSubGroupHeading>{formatMessage({ id: 'towingCommon.jobDetails.jobInfoHeader' })}</FormSubGroupHeading>
          </Column>
          <Column justify="flex-end">
            <RadioButtonGroup
              label={''}
              name="jobDetails.towingDateType"
              options={translatedOrderScheduleOptions}
            ></RadioButtonGroup>
          </Column>
        </Row>
      </FormSubGroupHeader>
      <FormSubGroup>
        {showTowingDateSpan ? (
          <Section columns={2}>
            <DateSectionColumn>
              <HalfDateInput
                label={formatMessage({ id: 'towingOrder.alTimeChangeModal.startDateLabel' })}
                name="jobDetails.towingDate"
                required
              />
              <Divider />
              <HalfDateInput
                label={formatMessage({ id: 'towingOrder.alTimeChangeModal.endDateLabel' })}
                name="jobDetails.towingEndDate"
              />
            </DateSectionColumn>

            <DateSectionColumn>
              <HalfTimeInput
                label={formatMessage({ id: 'towingOrder.alTimeChangeModal.startTimeLabel' })}
                name="jobDetails.towingTime"
              />
              <Divider />
              <HalfTimeInput
                label={formatMessage({ id: 'towingOrder.alTimeChangeModal.endTimeLabel' })}
                name="jobDetails.towingEndTime"
              />
            </DateSectionColumn>
          </Section>
        ) : (
          <Section columns={2} marginMobile="0">
            <DateSectionColumn>
              <HalfDateInput
                label={formatMessage({ id: 'towingCommon.jobDetails.towingDateLabel' })}
                name="jobDetails.towingDate"
                required
              />
              <HalfTimeInput
                label={formatMessage({ id: 'towingCommon.jobDetails.towingTimeLabel' })}
                name="jobDetails.towingTime"
              />
            </DateSectionColumn>
            <SectionColumn />
          </Section>
        )}

        <Section columns={2}>
          <SelectSectionColumn>
            <SelectInput
              label={formatMessage({ id: 'towingCommon.jobDetails.towingReasonLabel' })}
              name="jobDetails.towingReason"
              options={settings.towingReasons.map((label: string) => ({
                label,
                value: label,
              }))}
            />
            <SelectInput
              label={formatMessage({ id: 'towingCommon.jobDetails.preliminaryServiceTypeLabel' })}
              name="jobDetails.serviceType"
              options={translatedServiceTypeOptions}
            />
          </SelectSectionColumn>
          <DateSectionColumn>
            <HalfTextInput
              label={formatMessage({ id: 'towingCommon.jobDetails.orderReferenceLabel' })}
              name="jobDetails.orderReference"
            />
            <HalfTextInput
              label={formatMessage({ id: 'towingCommon.jobDetails.incidentCaseNoLabel' })}
              name="jobDetails.incidentCaseNo"
            />
          </DateSectionColumn>
        </Section>
        <Section columns={2}>
          <SectionColumn>
            <TextAreaInput
              label={formatMessage({ id: 'towingCommon.jobDetails.additionalInfoLabel' })}
              name="jobDetails.additionalInfo"
            />
          </SectionColumn>
          <SectionColumn>
            <TextAreaInput
              label={formatMessage({ id: 'towingCommon.jobDetails.notesLabel' })}
              name="jobDetails.notes"
            />
          </SectionColumn>
        </Section>
      </FormSubGroup>
    </FormSubGroupWrapper>
  )
}
